import { useMemo, useState, useCallback } from 'react'
import { useQuery, gql, ApolloError } from '@apollo/client'
import { useQueryVariables } from './store'
import { useSites } from 'store'

export interface WireLineFAI {
  id: string;
  name: string;
}

export interface WireLineHotspot {
  id: string;
  name: string;
  state: string;
}

export interface WireLineSite {
  id: string;
  name: string;
  city: string;
  state: string;
}

export interface WireLine {
  id: string;
  name: string;
  hotspot_id: string | null;
  hotspot: WireLineHotspot | null;
  wifirstsite: WireLineSite;
  connection_type: string | null;
  wire_type: string;
  use_case: string | null;
  owner_is_wifirst: boolean;
  fai: WireLineFAI;
  ip_address: string | null;
  effective_ip_address: string | null;
  download: number | null;
  upload: number | null;
  saturation: number | null;
  wan_access_through_ipsec: boolean;
  created_at: string;
  updated_at: string;
}

export interface WireLinesResponse {
  total: number;
  lines: WireLine[];
}

export interface GetWireLinesData {
  wireLines: WireLinesResponse;
}

export const OPERATION_FIND_WIRE_LINES = gql`
  query GetWireLines(
    $searchTerm: String
    $provider: String
    $dateFrom: String
    $dateTo: String
    $sortField: String
    $sortOrder: String
    $from: Int
    $size: Int
    $sfIds: [String]
  ) {
    wireLines: findWireLines(
      searchTerm: $searchTerm
      provider: $provider
      dateFrom: $dateFrom
      dateTo: $dateTo
      sortField: $sortField
      sortOrder: $sortOrder
      from: $from
      size: $size
      sfIds: $sfIds
    ) {
      total
      lines {
        id
        name
        hotspot_id
        hotspot {
          id
          name
          state
        }
        wifirstsite {
          id
          name
          city
          state
        }
        connection_type
        wire_type
        use_case
        owner_is_wifirst
        fai {
          id
          name
        }
        ip_address
        effective_ip_address
        download
        upload
        saturation
        wan_access_through_ipsec
        created_at
        updated_at
      }
    }
  }
`

const LIMIT = 10

interface UseSearchResult {
  error: ApolloError | undefined;
  loading: boolean;
  data?: GetWireLinesData;
  loadingNext: boolean;
  fetchNextPage: () => Promise<void>;
  shouldFetchNextPage: boolean;
}

const useSearch = (): UseSearchResult => {
  const [shouldFetchNextPage, setShouldFetchNextPage] = useState(true)
  const [{ selected }] = useSites()
  const sfIds = useMemo(() => (selected ? selected.map(site => site.sf_id) : []), [selected])
  const queryVariables = useQueryVariables()
  
  const currentFilters = useMemo(() => ({
    searchTerm: queryVariables.searchTerm,
    provider: queryVariables.provider,
    sortField: queryVariables.sortField,
    sortOrder: queryVariables.sortOrder,
    sfIds: JSON.stringify(sfIds)
  }), [queryVariables.searchTerm, queryVariables.provider, queryVariables.sortField, queryVariables.sortOrder, sfIds])
  
  const { error, loading, data, fetchMore, networkStatus, refetch } = useQuery<GetWireLinesData>(OPERATION_FIND_WIRE_LINES, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      ...queryVariables,
      sfIds: sfIds,
      from: 0,
      size: LIMIT
    }
  })
  
  useMemo(() => {
    setShouldFetchNextPage(true)
    refetch({
      ...queryVariables,
      sfIds: sfIds,
      from: 0,
      size: LIMIT
    })
  }, [currentFilters, queryVariables, sfIds, refetch])

  const shouldFetchMore = useMemo(() => {
    if (loading || !data) return false
    return data.wireLines.lines.length >= LIMIT && shouldFetchNextPage
  }, [loading, data, shouldFetchNextPage])

  const fetchNextPage = useCallback(async () => {
    if (!data) return
    
    try {
      const result = await fetchMore({
        variables: {
          ...queryVariables,
          sfIds: sfIds,
          from: data.wireLines.lines.length,
          size: LIMIT
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev
          
          return {
            wireLines: {
              ...fetchMoreResult.wireLines,
              lines: [...prev.wireLines.lines, ...fetchMoreResult.wireLines.lines]
            }
          }
        }
      })
      
      if (!result.data.wireLines.lines.length || result.data.wireLines.lines.length < LIMIT) {
        setShouldFetchNextPage(false)
      }
    } catch (error) {
      setShouldFetchNextPage(false)
    }
  }, [data, fetchMore, queryVariables, sfIds])

  return {
    error,
    loading: (loading && networkStatus !== 3) || networkStatus === 2,
    data,
    loadingNext: networkStatus === 3,
    fetchNextPage,
    shouldFetchNextPage: shouldFetchMore
  }
}

export default useSearch
